import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Box,
  Button,
  Paper,
  Typography,
  TextField,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  FormControl,
  Select,
  MenuItem,
  Chip,
  alpha,
} from '@mui/material';
import {
  Add as AddIcon,
  CloudUpload as UploadIcon,
  CheckCircle as CheckCircleIcon,
  ChatBubbleOutline as ChatIcon,
  ErrorOutline as ErrorIcon,
} from '@mui/icons-material';
import { DEFAULT_ALLOWED_MODELS } from '../config/modelConfig';

interface Category { id: string; name: string; }

const PUBLIC_RESPONSE_OBJECT_ID = 'f1000000-0000-4000-b000-000000000001';

const EXAMPLES = [
  {
    label: 'Store customer support',
    name: 'Store Help',
    instructions:
      'Answer customer questions about our products, orders, shipping and returns. Be friendly and brief. ' +
      "If you don't know the answer or the customer is upset, suggest they email our support team.",
  },
  {
    label: 'Product FAQ',
    name: 'Product FAQ',
    instructions:
      'Answer questions about how to set up and use our product, using the documents provided. Give short, ' +
      "step-by-step answers. Don't guess: if the documents don't cover it, say so.",
  },
  {
    label: 'Staff policy helper',
    name: 'Policy Helper',
    instructions:
      'Help employees find answers in our HR and company policies (leave, expenses, benefits). Quote the ' +
      'relevant policy section. For personal cases, tell them to contact HR directly.',
  },
];

interface ChatbotBuilderProps {
  onComplete?: (functionId: string) => void;
  onCancel?: () => void;
  initialName?: string;
  initialInstructions?: string;
}

export const ChatbotBuilder: React.FC<ChatbotBuilderProps> = ({ onComplete, onCancel, initialName, initialInstructions }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });

  // Basic Info
  const [chatbotName, setChatbotName] = useState(initialName ?? '');
  const [chatbotInstructions, setChatbotInstructions] = useState(initialInstructions ?? '');

  // Knowledge Base
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [newCategoryName, setNewCategoryName] = useState('');
  const [showNewCategoryDialog, setShowNewCategoryDialog] = useState(false);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<Array<{ 
    name: string; 
    status: 'uploading' | 'uploaded' | 'vectorizing' | 'vectorized' | 'failed';
    error?: string;
  }>>([]);

  // Final state
  const [createdFunction, setCreatedFunction] = useState<any>(null);

  // Load categories on mount - copied from MyMaterials.tsx
  const loadCategories = useCallback(async () => {
    try {
      setCategoriesLoading(true);
      const token = await user?.getIdToken();
      const response = await fetch(`/api/v2/customers/current`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) return;
      const current = await response.json();
      const customerId = current?.data?.customer_id;
      if (!customerId) return;
      const catsResp = await fetch(`/api/v2/customers/${customerId}/categories`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!catsResp.ok) return;
      const cats = await catsResp.json();
      const loadedCategories = cats.data?.items || [];
      setCategories(loadedCategories);
      
      // Auto-select category if one matches the chatbot name
      if (chatbotName.trim() && !selectedCategory && loadedCategories.length > 0) {
        const cleanChatbotName = chatbotName.trim().toLowerCase().replace(/[^a-z0-9\s]/g, '');
        const matchingCategory = loadedCategories.find((cat: Category) => 
          cat.name.toLowerCase().replace(/[^a-z0-9\s]/g, '') === cleanChatbotName
        );
        if (matchingCategory) {
          setSelectedCategory(matchingCategory.id);
        }
      }
    } catch (e) {
      console.error('Failed to load categories:', e);
    } finally {
      setCategoriesLoading(false);
    }
  }, [user, chatbotName, selectedCategory]);

  // Load categories on mount and when chatbot name changes (for auto-selection)
  useEffect(() => {
    loadCategories();
  }, [loadCategories]);
  
  // Reload categories when chatbot name changes (to check for matching category)
  useEffect(() => {
    if (chatbotName.trim() && categories.length > 0 && !selectedCategory) {
      const cleanChatbotName = chatbotName.trim().toLowerCase().replace(/[^a-z0-9\s]/g, '');
      const matchingCategory = categories.find((cat: Category) => 
        cat.name.toLowerCase().replace(/[^a-z0-9\s]/g, '') === cleanChatbotName
      );
      if (matchingCategory) {
        setSelectedCategory(matchingCategory.id);
        setSnackbar({ open: true, message: `Auto-selected category: ${matchingCategory.name}`, severity: 'success' });
      }
    }
  }, [chatbotName, categories, selectedCategory]);

  // Create new category - copied from MyMaterials.tsx
  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) {
      setSnackbar({ open: true, message: 'Category name cannot be empty', severity: 'error' });
      return;
    }
    
    try {
      const token = await user?.getIdToken();
      const currentResp = await fetch(`/api/v2/customers/current`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (!currentResp.ok) return;
      const current = await currentResp.json();
      const customerId = current?.data?.customer_id;
      if (!customerId) return;
      
      const resp = await fetch(`/api/v2/customers/${customerId}/categories`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name: newCategoryName.trim() })
      });
      
      if (!resp.ok) {
        const error = await resp.json();
        throw new Error(error.detail || 'Failed to create category');
      }
      
      const data = await resp.json();
      setSelectedCategory(data.data?.id);
      setSnackbar({ open: true, message: 'Category created successfully', severity: 'success' });
      setNewCategoryName('');
      setShowNewCategoryDialog(false);
      loadCategories();
    } catch (e: any) {
      setSnackbar({ open: true, message: e.message || 'Failed to create category', severity: 'error' });
    }
  };

  // Handle file selection - auto-upload and vectorize - copied from MyMaterials.tsx
  const handleFileSelect = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    try {
      setUploadingFiles(true);
      const token = await user?.getIdToken();
      
      // Get customer ID first
      const currentResp = await fetch(`/api/v2/customers/current`, { 
        headers: { 'Authorization': `Bearer ${token}` } 
      });
      if (!currentResp.ok) {
        throw new Error('Failed to get customer ID');
      }
      const current = await currentResp.json();
      const customerId = current?.data?.customer_id;
      if (!customerId) {
        throw new Error('No customer ID found');
      }
      
      // Auto-create category if none selected
      let categoryId = selectedCategory;
      if (!categoryId && chatbotName.trim()) {
        const cleanCategoryName = chatbotName.trim().replace(/[^a-zA-Z0-9\s]/g, '').substring(0, 50);

        const createCatResp = await fetch(`/api/v2/customers/${customerId}/categories`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ name: cleanCategoryName || 'Chatbot Knowledge' })
        });

        if (createCatResp.ok) {
          const catData = await createCatResp.json();
          categoryId = catData.data?.id;
          setSelectedCategory(categoryId);
          await loadCategories();
        }
      }

      if (!categoryId) {
        setSnackbar({ open: true, message: 'Please enter a chatbot name first or select a category', severity: 'error' });
        setUploadingFiles(false);
        return;
      }

      // Upload files one by one with status tracking (same pattern as MyMaterials.tsx)
      const fileArray = Array.from(files);
      const newFiles = fileArray.map(f => ({ name: f.name, status: 'uploading' as const }));
      setUploadedFiles(prev => [...prev, ...newFiles]);

      let successCount = 0;
      let failedCount = 0;
      const uploadedMaterialIds: Array<{ id: string; name: string }> = [];
      
      for (let i = 0; i < fileArray.length; i++) {
        const file = fileArray[i];
        const fileIndex = uploadedFiles.length + i;
        
        try {
          const formDataToSend = new FormData();
          formDataToSend.append('name', file.name);
          formDataToSend.append('description', `Knowledge for ${chatbotName || 'chatbot'}`);
          formDataToSend.append('file', file);
          formDataToSend.append('category_id', categoryId);

          const response = await fetch('/api/v2/materials/', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`
            },
            body: formDataToSend
          });

          if (response.ok) {
            successCount++;
            const data = await response.json();
            setUploadedFiles(prev => prev.map((f, idx) => 
              idx === fileIndex ? { ...f, status: 'uploaded' } : f
            ));
            
            if (data?.data?.id) {
              uploadedMaterialIds.push({ id: data.data.id, name: file.name });
            }
          } else {
            failedCount++;
            const errorData = await response.json();
            setUploadedFiles(prev => prev.map((f, idx) => 
              idx === fileIndex ? { ...f, status: 'failed', error: errorData.detail || 'Upload failed' } : f
            ));
          }
        } catch (error) {
          failedCount++;
          setUploadedFiles(prev => prev.map((f, idx) => 
            idx === fileIndex ? { ...f, status: 'failed', error: error instanceof Error ? error.message : 'Upload failed' } : f
          ));
        }
      }

      // Vectorize uploaded materials if any succeeded
      if (uploadedMaterialIds.length > 0) {
        setSnackbar({ open: true, message: 'Starting vectorization...', severity: 'success' });
        
        let vectorizeSuccess = 0;
        for (const material of uploadedMaterialIds) {
          const fileIndex = uploadedFiles.length + fileArray.findIndex(f => f.name === material.name);
          
          try {
            setUploadedFiles(prev => prev.map((f, idx) => 
              idx === fileIndex ? { ...f, status: 'vectorizing' } : f
            ));
            
            const resp = await fetch(`/api/v2/customers/${customerId}/materials/${material.id}/vectorize`, {
              method: 'POST',
              headers: { 
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({ enable: true })
            });
            
            if (resp.ok) {
              vectorizeSuccess++;
              setUploadedFiles(prev => prev.map((f, idx) => 
                idx === fileIndex ? { ...f, status: 'vectorized' } : f
              ));
            } else {
              setUploadedFiles(prev => prev.map((f, idx) => 
                idx === fileIndex ? { ...f, status: 'uploaded' } : f
              ));
            }
          } catch (e) {
            setUploadedFiles(prev => prev.map((f, idx) => 
              idx === fileIndex ? { ...f, status: 'uploaded' } : f
            ));
          }
        }
        
        if (vectorizeSuccess > 0) {
          setSnackbar({ open: true, message: `${vectorizeSuccess} material(s) queued for vectorization`, severity: 'success' });
        }
      }
      
      if (successCount > 0) {
        setSnackbar({ open: true, message: `Successfully uploaded ${successCount} file(s)${failedCount > 0 ? `, ${failedCount} failed` : ''}`, severity: 'success' });
      } else {
        setSnackbar({ open: true, message: 'Failed to upload files', severity: 'error' });
      }
    } catch (error) {
      setSnackbar({ open: true, message: error instanceof Error ? error.message : 'Failed to upload files', severity: 'error' });
    } finally {
      setUploadingFiles(false);
    }
  };

  // Create the chatbot function
  const handleCreateChatbot = async () => {
    if (!chatbotName.trim()) {
      setError('Please enter a chatbot name');
      return;
    }

    if (!chatbotInstructions.trim()) {
      setError('Please provide instructions for your chatbot');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const token = await user?.getIdToken();

      const instructions = `You are ${chatbotName}, a helpful AI assistant.\n\n${chatbotInstructions}\n\nPlease provide helpful and accurate responses based on these instructions. If knowledge documents are provided, use them to give accurate, detailed answers.`;

      // Build metadata
      const metadata: any = {
        is_chatbot: true,
        chatbot_name: chatbotName,
        created_by: 'chatbot_builder',
        chat_history_length: 50
      };
      if (selectedCategory) {
        metadata.knowledge_categories = [selectedCategory];
      }

      // Support multiple input types to allow file uploads
      const payload = {
        name: chatbotName.toLowerCase().replace(/[^a-z0-9]+/g, '_'),
        display_name: chatbotName,
        description: chatbotInstructions,
        instructions: instructions,
        input_types: ['text', 'image', 'video', 'audio', 'pdf', 'csv', 'markdown', 'html'],
        allowed_models: DEFAULT_ALLOWED_MODELS,
        response_type: 'text',
        response_object_id: PUBLIC_RESPONSE_OBJECT_ID,
        is_list_response: false,
        metadata
      };

      const response = await fetch('/api/v2/ai-functions/', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create chatbot');
      }

      const data = await response.json();
      setCreatedFunction(data);
      setSnackbar({ open: true, message: 'Chatbot created successfully!', severity: 'success' });
      
      // Call onComplete callback if provided
      if (onComplete && data.id) {
        onComplete(data.id);
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to create chatbot');
    } finally {
      setLoading(false);
    }
  };

  if (createdFunction) {
    return (
      <Paper variant="outlined" sx={{ maxWidth: 820, mx: 'auto', p: 4, textAlign: 'center', borderRadius: 2 }}>
        <CheckCircleIcon sx={{ fontSize: 56, color: 'success.main', mb: 1 }} />
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
          {chatbotName} is ready
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Open it to try it out, match your brand colors and add it to your website.
        </Typography>
        <Button variant="contained" onClick={() => navigate(`/chatbot/studio/${createdFunction.id}`)}>
          Open chatbot
        </Button>
      </Paper>
    );
  }

  const canUpload = !uploadingFiles && (!!selectedCategory || !!chatbotName.trim());
  const canCreate = !loading && !uploadingFiles && !!chatbotName.trim() && !!chatbotInstructions.trim();

  return (
    <Paper
      variant="outlined"
      component="section"
      aria-labelledby="new-chatbot-title"
      sx={(theme) => ({
        p: { xs: 2.5, md: 3.5 },
        borderRadius: 2,
        borderColor: alpha(theme.palette.primary.main, 0.35),
        bgcolor: alpha(theme.palette.primary.main, 0.03),
      })}
    >
      <Box sx={{ maxWidth: 820, mx: 'auto' }}>
        <Typography id="new-chatbot-title" variant="h5" component="h2" sx={{ fontWeight: 700, mb: 0.5 }}>
          New chatbot
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
          Name it and say what it should help with. You can change everything later, try it out, and match your
          brand before it goes on your website.
        </Typography>

        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TextField
          fullWidth
          label="Name"
          placeholder="E.g. Acme Help"
          value={chatbotName}
          onChange={(e) => setChatbotName(e.target.value)}
          helperText="Shown at the top of the chat window."
          sx={{ mb: 2, bgcolor: 'background.paper' }}
          required
        />

        <TextField
          fullWidth
          multiline
          minRows={4}
          maxRows={12}
          label="What should it help with?"
          placeholder="E.g. Answer customer questions about our products, shipping and returns. Be friendly and brief. If you don't know, suggest emailing support@acme.com."
          value={chatbotInstructions}
          onChange={(e) => setChatbotInstructions(e.target.value)}
          helperText="Who it talks to, what it should and shouldn't answer, its tone, and what to do when it doesn't know."
          sx={{ bgcolor: 'background.paper' }}
          required
        />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 1.5, mb: 3 }}>
          <Typography variant="caption" color="text.secondary">
            Try an example:
          </Typography>
          {EXAMPLES.map((ex) => (
            <Chip
              key={ex.label}
              label={ex.label}
              size="small"
              variant="outlined"
              onClick={() => {
                setChatbotName(ex.name);
                setChatbotInstructions(ex.instructions);
              }}
            />
          ))}
        </Box>

        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          Documents it should answer from{' '}
          <Typography component="span" variant="body2" color="text.secondary">
            (optional)
          </Typography>
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Help articles, policies, manuals, price lists. They're saved to a Knowledge Hub category and the chatbot
          searches them on every message.
        </Typography>

        <Box sx={{ display: 'flex', gap: 1, mb: 1.5, alignItems: 'center' }}>
          <FormControl fullWidth size="small">
            <Select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              displayEmpty
              disabled={categoriesLoading}
              sx={{ bgcolor: 'background.paper' }}
              inputProps={{ 'aria-label': 'Knowledge Hub category' }}
            >
              <MenuItem value="">
                {categoriesLoading ? 'Loading categories…' : 'New category named after the chatbot'}
              </MenuItem>
              {categories.map((cat) => (
                <MenuItem key={cat.id} value={cat.id}>
                  {cat.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => setShowNewCategoryDialog(true)}
            sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
          >
            New category
          </Button>
        </Box>

        <Box
          onDrop={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (canUpload && e.dataTransfer.files) {
              handleFileSelect(e.dataTransfer.files);
            }
          }}
          onDragOver={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onDragEnter={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          sx={(theme) => ({
            border: '2px dashed',
            borderColor: uploadedFiles.length > 0 ? 'primary.main' : 'divider',
            borderRadius: 2,
            p: 2.5,
            textAlign: 'center',
            bgcolor: 'background.paper',
            opacity: canUpload || uploadingFiles ? 1 : 0.6,
            transition: 'all 0.2s',
            '&:hover': canUpload ? { borderColor: 'primary.main', bgcolor: alpha(theme.palette.primary.main, 0.04) } : {},
          })}
        >
          <input
            type="file"
            multiple
            onChange={(e) => handleFileSelect(e.target.files)}
            style={{ display: 'none' }}
            id="knowledge-file-upload"
            accept="*/*"
            disabled={!canUpload}
          />
          <label
            htmlFor="knowledge-file-upload"
            style={{ cursor: uploadingFiles ? 'wait' : canUpload ? 'pointer' : 'not-allowed', display: 'block' }}
          >
            <UploadIcon sx={{ fontSize: 36, color: 'text.secondary', mb: 0.5 }} />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {uploadingFiles
                ? 'Uploading…'
                : canUpload
                  ? 'Drop files here or click to choose'
                  : 'Enter a name first (or pick a category) to add files'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              PDF, Word, text, Markdown, PowerPoint, Excel, CSV, images, audio and video.
            </Typography>
          </label>
        </Box>

        {uploadedFiles.length > 0 && (
          <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {uploadedFiles.map((file, idx) => (
              <Box
                key={idx}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 1.5,
                  py: 1,
                  bgcolor: 'background.paper',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: file.status === 'failed' ? 'error.main' : 'divider',
                }}
              >
                {(file.status === 'uploading' || file.status === 'vectorizing') && <CircularProgress size={14} />}
                {(file.status === 'uploaded' || file.status === 'vectorized') && (
                  <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
                )}
                {file.status === 'failed' && <ErrorIcon sx={{ fontSize: 16, color: 'error.main' }} />}
                <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>
                  {file.name}
                </Typography>
                {file.error && (
                  <Typography variant="caption" color="error">
                    {file.error}
                  </Typography>
                )}
                <Chip
                  size="small"
                  label={
                    file.status === 'uploading' ? 'Uploading…' :
                    file.status === 'uploaded' ? 'Uploaded' :
                    file.status === 'vectorizing' ? 'Preparing…' :
                    file.status === 'vectorized' ? 'Ready' :
                    'Failed'
                  }
                  color={file.status === 'vectorized' ? 'success' : file.status === 'failed' ? 'error' : 'default'}
                />
              </Box>
            ))}
          </Box>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mt: 3 }}>
          <Button
            variant="contained"
            size="large"
            onClick={handleCreateChatbot}
            disabled={!canCreate}
            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <ChatIcon />}
          >
            {loading ? 'Creating…' : 'Create chatbot'}
          </Button>
          {onCancel && (
            <Button onClick={onCancel} disabled={loading}>
              Cancel
            </Button>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ ml: { sm: 'auto' } }}>
            {uploadingFiles ? 'Wait for uploads to finish.' : 'Next you can try it and customize how it looks.'}
          </Typography>
        </Box>
      </Box>

      <Dialog open={showNewCategoryDialog} onClose={() => setShowNewCategoryDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>New Knowledge Hub category</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Category name"
            value={newCategoryName}
            onChange={(e) => setNewCategoryName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleCreateCategory();
            }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowNewCategoryDialog(false)}>Cancel</Button>
          <Button onClick={handleCreateCategory} variant="contained">Create</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};
